import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './bulkHideChildrenDialog.module.scss'
import { useMutation } from '@apollo/client'
import { UPDATE_CHILDREN_VISIBILITY } from '@/modules/API/parent/childHandler'
import { useNotification } from '@/modules/context/NotificationContext'

interface BulkHideChildrenDialogProps {
    isDialogOpen: boolean
    handleCloseDialog: () => void
    selectedChildIds: number[]
    daycareId: number
    onSuccess: () => void
}

const BulkHideChildrenDialog: FunctionComponent<BulkHideChildrenDialogProps> = ({
    isDialogOpen,
    handleCloseDialog,
    selectedChildIds,
    daycareId,
    onSuccess
}: BulkHideChildrenDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const [updateChildrenVisibility] = useMutation(UPDATE_CHILDREN_VISIBILITY)

    async function handleConfirm(): Promise<void> {
        setIsSubmitting(true)

        try {
            await updateChildrenVisibility({
                variables: {
                    daycare_id: daycareId,
                    child_ids: selectedChildIds,
                    visibility: false
                }
            })

            showNotificationSuccess(t('bulkHideChildsSuccess'))
            onSuccess()
            handleCloseDialog()
        } catch {
            showNotificationError(t('bulkHideChildsError'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={isDialogOpen} handleClose={handleCloseDialog} className={styles.Dialog}>
            <DialogHeader title={t('bulkHideChilds')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {selectedChildIds.length} {selectedChildIds.length > 1 ? t('childsSelected') : t('childSelected')}
                    {' - '}
                    {t('confirmBulkHideChilds')}
                </p>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'confirm'}
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'cancel'}
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default BulkHideChildrenDialog
